import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AppShell } from "@/components/AppShell";
import { DisclaimerNote } from "@/components/DisclaimerNote";
import { api, apiError, FRAMEWORK_LABELS, FRAMEWORK_KEYS, STATUS_LABELS } from "@/lib/api";
import {
  CATEGORY_HELP,
  CONNECTIVITY_HELP,
  DEPLOYMENT_HELP,
  CAPABILITY_HELP,
  INTERFACE_HELP,
  MARKET_HELP,
} from "@/lib/glossary";
import { Spinner } from "@/components/Spinner";

const label = (v) => v.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const Hint = ({ children, testId }) =>
  children ? (
    <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500" data-testid={testId}>
      {children}
    </p>
  ) : null;

const OptionRow = ({ name, help }) => (
  <span className="block max-w-[340px] whitespace-normal break-words">
    <span className="text-slate-800">{name}</span>
    {help ? <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-500">{help}</span> : null}
  </span>
);

export default function NewProduct() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "industrial_control",
    connectivity: "local_network",
    capabilities: [],
    markets: ["eu"],
    product_version: "",
    firmware_version: "",
    deployment_model: "on_premise_industrial",
    interfaces: [],
  });

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  // Applicability comes from the backend engine so the preview can never drift from scoring.
  useEffect(() => {
    const t = setTimeout(() => {
      api
        .post("/applicability/preview", { ...form, name: form.name.trim() || "Draft product" })
        .then(({ data }) => setPreview(data.applicability))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [form]);

  const toggle = (field, value) =>
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(value) ? f[field].filter((x) => x !== value) : [...f[field], value],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/products", form);
      toast.success("Product profile created");
      navigate(`/assessment/${data.assessment_id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!meta)
    return (
      <AppShell breadcrumb="products / new">
        <Spinner label="Loading product options" />
      </AppShell>
    );

  const applicability = preview || {};

  return (
    <AppShell breadcrumb="products / new">
      <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr]">
        <form onSubmit={submit} className="space-y-8" data-testid="product-form">
          <div>
            <p className="overline text-navy-800">Step 1 · Product profile</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-900">Describe the product</h1>
            <p className="mt-2 max-w-xl text-sm text-slate-600">
              These answers drive applicability: which frameworks are shown, scored, or marked out of
              scope for this product. Each option explains itself, so pick the closest match rather
              than the most flattering one.
            </p>
          </div>

          <div className="space-y-5 border border-slate-200 bg-white p-6">
            <div>
              <Label htmlFor="name" className="text-xs">Product name</Label>
              <Input
                id="name"
                required
                minLength={2}
                placeholder="e.g. SG-400 Edge Gateway"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description" className="text-xs">Short description (optional)</Label>
              <Textarea
                id="description"
                rows={3}
                placeholder="What the product does, where it is deployed, notable interfaces"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="product-description-input"
              />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Product category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-w-[380px]">
                    {meta.categories.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>
                        <OptionRow name={label(c)} help={CATEGORY_HELP[c]} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Hint testId="category-hint">{CATEGORY_HELP[form.category]}</Hint>
              </div>
              <div>
                <Label className="text-xs">Connectivity</Label>
                <Select value={form.connectivity} onValueChange={(v) => setForm({ ...form, connectivity: v })}>
                  <SelectTrigger data-testid="product-connectivity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-w-[380px]">
                    {meta.connectivity.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`connectivity-option-${c}`}>
                        <OptionRow name={label(c)} help={CONNECTIVITY_HELP[c]} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Hint testId="connectivity-hint">{CONNECTIVITY_HELP[form.connectivity]}</Hint>
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="product_version" className="text-xs">Product version * (required for the full report)</Label>
                <Input
                  id="product_version"
                  required
                  placeholder="e.g. Rev C"
                  value={form.product_version}
                  onChange={(e) => setForm({ ...form, product_version: e.target.value })}
                  data-testid="product-version-input"
                />
              </div>
              <div>
                <Label htmlFor="firmware_version" className="text-xs">Firmware version</Label>
                <Input
                  id="firmware_version"
                  placeholder="e.g. 4.2.1"
                  value={form.firmware_version}
                  onChange={(e) => setForm({ ...form, firmware_version: e.target.value })}
                  data-testid="firmware-version-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Deployment model</Label>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                Where the product physically ends up, and who controls that environment. This drives how
                harshly exposure-related gaps are weighted.
              </p>
              <Select value={form.deployment_model} onValueChange={(v) => setForm({ ...form, deployment_model: v })}>
                <SelectTrigger className="mt-2" data-testid="deployment-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-w-[380px]">
                  {(meta.deployment_models || []).map((d) => (
                    <SelectItem key={d} value={d} data-testid={`deployment-option-${d}`}>
                      <OptionRow name={label(d)} help={DEPLOYMENT_HELP[d]} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Hint testId="deployment-hint">{DEPLOYMENT_HELP[form.deployment_model]}</Hint>
            </div>
          </div>

          <div className="border border-slate-200 bg-white p-6">
            <Label className="text-xs">
              External interfaces * (at least one required for the full report)
            </Label>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
              Every way data or a person can get into the product, including service and debug access.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {(meta.interfaces || []).map((i) => (
                <label key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Checkbox
                    checked={form.interfaces.includes(i)}
                    onCheckedChange={() => toggle("interfaces", i)}
                    data-testid={`interface-${i}`}
                  />
                  <span>
                    {label(i)}
                    <span className="block text-[11px] leading-relaxed text-slate-500">
                      {INTERFACE_HELP[i]}
                    </span>
                  </span>
                </label>
              ))}
            </div>

          </div>

          <div className="border border-slate-200 bg-white p-6">
            <Label className="text-xs">Product capabilities</Label>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
              Tick everything the product can do. These decide which framework obligations apply.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {meta.capabilities.map((c) => (
                <label key={c} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Checkbox
                    checked={form.capabilities.includes(c)}
                    onCheckedChange={() => toggle("capabilities", c)}
                    data-testid={`capability-${c}`}
                  />
                  <span>
                    {label(c)}
                    <span className="block text-[11px] leading-relaxed text-slate-500">
                      {CAPABILITY_HELP[c]}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="border border-slate-200 bg-white p-6">
            <Label className="text-xs">Target markets</Label>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
              Where the product is placed on the market. This is what makes a regime applicable or out
              of scope.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {meta.markets.map((m) => (
                <label key={m} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Checkbox
                    checked={form.markets.includes(m)}
                    onCheckedChange={() => toggle("markets", m)}
                    data-testid={`market-${m}`}
                  />
                  <span>
                    {m.toUpperCase()}
                    <span className="block text-[11px] leading-relaxed text-slate-500">
                      {MARKET_HELP[m]}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <DisclaimerNote text={meta.disclaimer} testId="pre-assessment-disclaimer" />

          <Button
            type="submit"
            disabled={busy}
            size="lg"
            className="bg-navy-800 text-white hover:bg-navy-700"
            data-testid="create-product-button"
          >
            {busy ? "Creating…" : "Create profile and start assessment"} <ArrowRight size={16} className="ml-2" />
          </Button>
        </form>

        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="border border-slate-200 bg-white p-6" data-testid="applicability-preview">
            <p className="overline text-navy-800">Live applicability preview</p>
            <div className="mt-5 space-y-4">
              {FRAMEWORK_KEYS.map((key) => {
                const entry = applicability[key];
                const status = entry?.status || "limited";
                return (
                  <div
                    key={key}
                    className="border-l-2 pl-3"
                    style={{ borderColor: status === "applicable" ? "#1E3A8A" : status === "limited" ? "#B45309" : "#CBD5E1" }}
                  >
                    <p className="font-mono text-[11px] uppercase tracking-wide text-slate-500">
                      {FRAMEWORK_LABELS[key]}
                    </p>
                    <p
                      className={`mt-1 text-sm font-semibold ${
                        status === "applicable" ? "text-navy-800" : status === "limited" ? "text-amber-700" : "text-slate-400"
                      }`}
                      data-testid={`preview-status-${key}`}
                    >
                      {entry ? STATUS_LABELS[status] : "Calculating…"}
                    </p>
                    {entry?.rationale && (
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-500" data-testid={`preview-rationale-${key}`}>
                        {entry.rationale}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-xs leading-relaxed text-slate-500">
              Frameworks marked not applicable are hidden from scoring and from the report for this
              product profile.
            </p>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
